import React from "react";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import type { ButtonProps } from "@mui/material";
import { getDesignTokens } from "@/theme/designTokens";
import { primaryActionSx } from "@/theme/primary-action";

type AppButtonOwnProps = {
  variantType?: "primary" | "neutral" | "filter";
  isActive?: boolean;
  /** React Router destination when `component={Link}`. */
  to?: string;
};

export type AppButtonProps = ButtonProps & AppButtonOwnProps;

/** Ref-forwarding so callers can drive focus (e.g. returning focus after a dialog closes). */
export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(function AppButton(
  { variantType = "primary", isActive = false, sx, ...props },
  ref
) {
  const theme = useTheme();
  const tokens = getDesignTokens(theme);

  const styles =
    variantType === "primary"
      ? primaryActionSx
      : variantType === "filter"
      ? {
          backgroundColor: `${isActive ? tokens.selection.surfaceSolid : tokens.surface} !important`,
          color: `${isActive ? tokens.selection.textOnSolid : tokens.textPrimary} !important`,
          border: `1px solid ${tokens.border}`,
          "&:hover": {
            backgroundColor: isActive ? tokens.button.primary.hover : tokens.button.neutral.hover,
          },
        }
      : {
          backgroundColor: tokens.surface,
          color: tokens.textPrimary,
          border: `1px solid ${tokens.border}`,
          "&:hover": {
            backgroundColor: tokens.button.neutral.hover,
          },
        };

  return (
    <Button
      ref={ref}
      {...(props as ButtonProps)}
      sx={[
        {
          borderRadius: "999px",
          textTransform: "none",
        },
        styles,
        ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
});

export default AppButton;
